import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { LocalizationModule } from '@abp/ng.core';
import { ThemeSharedModule, ToasterService } from '@abp/ng.theme.shared';
import { ImpersonationService } from '../../services/impersonation.service';
import { ImpersonatableUserDto } from '../../models/impersonation.models';

@Component({
  selector: 'app-impersonation-page',
  standalone: true,
  imports: [CommonModule, FormsModule, LocalizationModule, ThemeSharedModule],
  template: `
    <div class="card">
      <div class="card-header">
        <div class="row">
          <div class="col col-md-6">
            <h5 class="card-title">
              <i class="fas fa-user-secret me-2"></i>
              {{ '::Impersonation:Title' | abpLocalization }}
            </h5>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div class="mb-3">
          <input
            type="text"
            class="form-control"
            [placeholder]="'::Impersonation:SearchPlaceholder' | abpLocalization"
            [(ngModel)]="searchTerm"
            (ngModelChange)="onSearchChange($event)"
          />
        </div>

        <p class="text-muted mb-3">{{ '::Impersonation:SelectUser' | abpLocalization }}</p>

        <div *ngIf="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <div *ngIf="!loading && filteredUsers.length === 0" class="text-center py-5 text-muted">
          <i class="fas fa-users fa-3x mb-3 d-block"></i>
          {{ '::Impersonation:NoUsersFound' | abpLocalization }}
        </div>

        <div *ngIf="!loading && filteredUsers.length > 0" class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>{{ 'Users' | abpLocalization }}</th>
                <th>{{ 'Email' | abpLocalization }}</th>
                <th>{{ 'Roles' | abpLocalization }}</th>
                <th class="text-end">{{ 'Actions' | abpLocalization }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let user of filteredUsers">
                <td>
                  <div class="fw-semibold">{{ user.fullName || user.name + ' ' + user.surname }}</div>
                  <div class="text-muted small">@{{ user.userName }}</div>
                </td>
                <td>{{ user.email }}</td>
                <td>
                  <span class="badge bg-secondary me-1" *ngFor="let role of user.roles">{{ role }}</span>
                </td>
                <td class="text-end">
                  <button
                    class="btn btn-sm btn-primary"
                    [disabled]="submitting === user.id"
                    (click)="startImpersonation(user)"
                  >
                    <span *ngIf="submitting === user.id" class="spinner-border spinner-border-sm me-1"></span>
                    {{ '::Impersonation:StartButton' | abpLocalization }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `],
})
export class ImpersonationPageComponent implements OnInit, OnDestroy {
  users: ImpersonatableUserDto[] = [];
  filteredUsers: ImpersonatableUserDto[] = [];
  searchTerm = '';
  loading = false;
  submitting: string | null = null;

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(
    private impersonationService: ImpersonationService,
    private toaster: ToasterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(term => {
        this.filterUsers(term);
      });

    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUsers(): void {
    this.loading = true;
    this.impersonationService.getImpersonatableUsers().subscribe({
      next: result => {
        this.users = result.items;
        this.filteredUsers = result.items;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toaster.error('::Impersonation:Error:NotAllowed');
      },
    });
  }

  onSearchChange(term: string): void {
    this.searchSubject.next(term);
  }

  private filterUsers(term: string): void {
    if (!term) {
      this.filteredUsers = this.users;
      return;
    }

    const lowerTerm = term.toLowerCase();
    this.filteredUsers = this.users.filter(
      user =>
        user.name?.toLowerCase().includes(lowerTerm) ||
        user.surname?.toLowerCase().includes(lowerTerm) ||
        user.email?.toLowerCase().includes(lowerTerm) ||
        user.userName?.toLowerCase().includes(lowerTerm) ||
        user.fullName?.toLowerCase().includes(lowerTerm)
    );
  }

  startImpersonation(user: ImpersonatableUserDto): void {
    this.submitting = user.id;
    this.impersonationService.startImpersonation(user.id).subscribe({
      next: result => {
        this.submitting = null;
        if (result.success) {
          this.toaster.success('::Impersonation:Started');
          this.router.navigate(['/']);
        } else {
          this.toaster.error(result.errorMessage || '::Impersonation:Error:NotAllowed');
        }
      },
      error: err => {
        this.submitting = null;
        const errorMessage = err.error?.error?.message || '::Impersonation:Error:NotAllowed';
        this.toaster.error(errorMessage);
      },
    });
  }
}
